import { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';
import { AGENT_MATCHALLTAGS_FIELD_NAME, TAG_AND, TAGGED_MODE } from '../../ServiceTestsConstants';
import useAgentsContext from './AgentsContextProvider';

/**
 * Tag-based agent filtering (AND/OR) with matching agent computation.
 *
 * Manages the tagOp local UI state and computes which available agents
 * match the selected tags. Only writes matchAllTags (the backend field) to the form.
 *
 * @state-ownership
 * - Receives: tagOpRef from orchestrator (useRef, synced here)
 * - Reads from RHF: nothing directly (selectedTags passed as param)
 * - Writes to RHF: agents.matchAllTags via setValueRef
 * - Params: { mode, selectedTags, setValueRef, tagOpRef, initialTagOp }
 */
export function useTagMatching({ mode, selectedTags, setValueRef, tagOpRef, initialTagOp }) {
    const { availableAgents, setMatchingTaggedAgents } = useAgentsContext();

    const [tagOp, setTagOp] = useState(initialTagOp);
    tagOpRef.current = tagOp;

    // Update matching agents preview when tags, tagOp, or mode changes.
    // Mirror portal/ui tag matching: uses availableAgents directly (already filtered by server + client).
    useEffect(() => {
        if (mode !== TAGGED_MODE || isEmpty(selectedTags)) {
            setMatchingTaggedAgents([]);
            return;
        }

        const matched = availableAgents.filter((agent) => {
            const agentTagList = agent.tags || [];
            if (tagOp === TAG_AND) {
                return selectedTags.every((tag) => agentTagList.includes(tag));
            }
            return selectedTags.some((tag) => agentTagList.includes(tag));
        });
        setMatchingTaggedAgents(matched || []);
    }, [selectedTags, tagOp, mode, availableAgents, setMatchingTaggedAgents]);

    // Update local tagOp state and write matchAllTags (the only backend field) to the form
    const handleTagOpChange = useCallback(
        (newTagOp) => {
            setTagOp(newTagOp);
            setValueRef.current(AGENT_MATCHALLTAGS_FIELD_NAME, newTagOp === TAG_AND, SHOULD_DIRTY_TRUE);
        },
        [setValueRef]
    );

    return { tagOp, handleTagOpChange };
}
